// mentalWellnessScreeningData.js
// All screen definitions, questions, options and scoring for Mental Wellness Screening

export const MENTAL_STEPS = [
  { id: 'intro',      label: 'Introduction' },
  { id: 'consent',    label: 'Consent' },
  { id: 'concerns',   label: 'Main Concerns' },
  { id: 'mood',       label: 'Mood & Emotions' },
  { id: 'anxiety',    label: 'Anxiety & Worry' },
  { id: 'sleep',      label: 'Sleep & Rest' },
  { id: 'energy',     label: 'Energy & Motivation' },
  { id: 'social',     label: 'Social & Daily Life' },
  { id: 'coping',     label: 'Coping & Support' },
  { id: 'review',     label: 'Review Summary' },
  { id: 'result',     label: 'Wellness Result' },
];

export const MENTAL_FLOW = [
  'intro', 'consent', 'concerns',
  'mood', 'anxiety', 'sleep', 'energy', 'social', 'coping',
  'review', 'result',
];

// ── Intro ──────────────────────────────────────────────────────────────────────
export const MENTAL_INTRO = {
  badge:        'Mental Wellness Check',
  badgeIcon:    '🧠',
  heading:      'How are you really feeling?',
  desc:         'A confidential check-in on your mental and emotional wellbeing, designed to help you understand your needs and next steps.',
  bullets: [
    { icon: '⏱️', text: 'Takes less than 6 minutes' },
    { icon: '🔒', text: 'Private & confidential' },
    { icon: '💛', text: 'Supportive, not diagnostic' },
  ],
  privacy:      'Your answers are completely private and will never be shared without your consent.',
  startLabel:   'Start check-in',
  laterLabel:   "I'll do it later",
  illustrationKey: 'mental',
};

// ── Consent ────────────────────────────────────────────────────────────────────
export const MENTAL_CONSENT = {
  heading: 'A few things to know',
  paragraphs: [
    'This check-in helps identify areas where you might benefit from additional support. It is not a clinical diagnosis.',
    'If you are in crisis or immediate distress, please contact emergency services or a crisis helpline right away.',
  ],
  checkLabel: 'I understand this is for informational purposes only and does not replace professional mental health care.',
  agreeLabel: 'I understand, continue',
};

// ── Concern chips ──────────────────────────────────────────────────────────────
export const MENTAL_CONCERNS = [
  { id: 'lowMood',      label: 'Low mood',          icon: '😔' },
  { id: 'anxiety',      label: 'Anxiety',            icon: '😰' },
  { id: 'stress',       label: 'Stress',             icon: '😤' },
  { id: 'sleepIssues',  label: 'Sleep problems',     icon: '😴' },
  { id: 'loneliness',   label: 'Loneliness',         icon: '🤍' },
  { id: 'burnout',      label: 'Burnout',            icon: '🪫' },
  { id: 'selfEsteem',   label: 'Self-esteem',        icon: '🪞' },
  { id: 'other',        label: 'Something else',     icon: '···' },
];

// ── Question screens ───────────────────────────────────────────────────────────
export const MENTAL_QUESTION_SCREENS = [
  {
    id: 'mood',
    stepLabel: 'Mood & Emotions',
    title: 'Mood & Emotions',
    subtitle: 'How you have been feeling emotionally over the past two weeks.',
    icon: '💛',
    questions: [
      {
        id: 'overallMood',
        label: 'Overall, how would you describe your mood lately?',
        type: 'toggle',
        options: [
          { label: 'Positive & stable',  score: 0 },
          { label: 'Mostly okay',        score: 1 },
          { label: 'Up and down',        score: 2 },
          { label: 'Low or flat',        score: 3 },
          { label: 'Very low or empty',  score: 4 },
        ],
      },
      {
        id: 'enjoyment',
        label: 'How often do you feel little interest or pleasure in activities you used to enjoy?',
        type: 'toggle',
        options: [
          { label: 'Not at all',       score: 0 },
          { label: 'Several days',     score: 1 },
          { label: 'More than half',   score: 2 },
          { label: 'Nearly every day', score: 3 },
        ],
      },
      {
        id: 'hopelessness',
        label: 'Have you felt hopeless or like things will not get better?',
        type: 'toggle',
        options: [
          { label: 'Not at all',       score: 0 },
          { label: 'A few times',      score: 1 },
          { label: 'Often',            score: 2 },
          { label: 'Nearly every day', score: 3 },
        ],
      },
      {
        id: 'selfHarmThoughts',
        label: 'Have you had any thoughts of harming yourself?',
        type: 'toggle',
        redFlag: true,
        options: [
          { label: 'No, not at all',        score: 0 },
          { label: 'Fleeting thoughts only', score: 2 },
          { label: 'Sometimes',             score: 3 },
          { label: 'Often',                 score: 4 },
        ],
      },
    ],
  },

  {
    id: 'anxiety',
    stepLabel: 'Anxiety & Worry',
    title: 'Anxiety & Worry',
    subtitle: 'Anxiety is very common and can take many forms. There are no right or wrong answers.',
    icon: '😰',
    questions: [
      {
        id: 'worryFreq',
        label: 'How often do you feel nervous, anxious or on edge?',
        type: 'toggle',
        options: [
          { label: 'Not at all',       score: 0 },
          { label: 'Several days',     score: 1 },
          { label: 'More than half',   score: 2 },
          { label: 'Nearly every day', score: 3 },
        ],
      },
      {
        id: 'worryControl',
        label: 'How difficult is it to control your worrying?',
        type: 'toggle',
        options: [
          { label: 'Not difficult',           score: 0 },
          { label: 'Somewhat difficult',      score: 1 },
          { label: 'Very difficult',          score: 2 },
          { label: 'Extremely difficult',     score: 3 },
        ],
      },
      {
        id: 'panicAttacks',
        label: 'Have you experienced sudden intense fear or panic attacks?',
        type: 'toggle',
        options: [
          { label: 'Never',          score: 0 },
          { label: 'Once or twice',  score: 1 },
          { label: 'Several times',  score: 2 },
          { label: 'Frequently',     score: 3 },
        ],
      },
      {
        id: 'avoidance',
        label: 'Do you avoid situations, places or people due to anxiety?',
        type: 'toggle',
        options: [
          { label: 'Never',          score: 0 },
          { label: 'Occasionally',   score: 1 },
          { label: 'Often',          score: 2 },
          { label: 'Almost always',  score: 3 },
        ],
      },
    ],
  },

  {
    id: 'sleep',
    stepLabel: 'Sleep & Rest',
    title: 'Sleep & Rest',
    subtitle: 'Sleep quality is deeply connected to mental and emotional health.',
    icon: '😴',
    questions: [
      {
        id: 'sleepQuality',
        label: 'How would you rate your sleep quality overall?',
        type: 'toggle',
        options: [
          { label: 'Very good',   score: 0 },
          { label: 'Good',        score: 1 },
          { label: 'Fair',        score: 2 },
          { label: 'Poor',        score: 3 },
          { label: 'Very poor',   score: 4 },
        ],
      },
      {
        id: 'sleepIssueType',
        label: 'What sleep difficulties do you experience most?',
        type: 'toggle',
        options: [
          { label: 'None',              score: 0 },
          { label: 'Falling asleep',    score: 2 },
          { label: 'Staying asleep',    score: 2 },
          { label: 'Waking too early',  score: 2 },
          { label: 'All of the above',  score: 3 },
        ],
      },
      {
        id: 'sleepImpact',
        label: 'How much does poor sleep affect your daytime functioning?',
        type: 'toggle',
        options: [
          { label: 'Not at all',        score: 0 },
          { label: 'A little',          score: 1 },
          { label: 'Moderately',        score: 2 },
          { label: 'Significantly',     score: 3 },
        ],
      },
    ],
  },

  {
    id: 'energy',
    stepLabel: 'Energy & Motivation',
    title: 'Energy & Motivation',
    subtitle: 'Low energy and motivation are common signs of emotional strain.',
    icon: '⚡',
    questions: [
      {
        id: 'energyLevel',
        label: 'How would you describe your energy levels day-to-day?',
        type: 'toggle',
        options: [
          { label: 'High — I feel energised',   score: 0 },
          { label: 'Moderate — manageable',     score: 1 },
          { label: 'Low — often tired',         score: 2 },
          { label: 'Very low — exhausted',      score: 3 },
        ],
      },
      {
        id: 'motivation',
        label: 'How often do you struggle to motivate yourself to do daily tasks?',
        type: 'toggle',
        options: [
          { label: 'Rarely',        score: 0 },
          { label: 'Sometimes',     score: 1 },
          { label: 'Often',         score: 2 },
          { label: 'Almost always', score: 3 },
        ],
      },
      {
        id: 'concentration',
        label: 'How is your concentration and ability to focus?',
        type: 'toggle',
        options: [
          { label: 'Sharp and clear',     score: 0 },
          { label: 'Mostly fine',         score: 1 },
          { label: 'Often distracted',    score: 2 },
          { label: 'Very hard to focus',  score: 3 },
        ],
      },
    ],
  },

  {
    id: 'social',
    stepLabel: 'Social & Daily Life',
    title: 'Social & Daily Life',
    subtitle: 'How your mental wellbeing is affecting your relationships and daily activities.',
    icon: '🤝',
    questions: [
      {
        id: 'socialWithdrawal',
        label: 'Have you been withdrawing from friends, family or social activities?',
        type: 'toggle',
        options: [
          { label: 'Not at all',     score: 0 },
          { label: 'A little',       score: 1 },
          { label: 'Quite a bit',    score: 2 },
          { label: 'Significantly',  score: 3 },
        ],
      },
      {
        id: 'dailyFunctioning',
        label: 'How much are your emotions affecting your work or daily responsibilities?',
        type: 'toggle',
        options: [
          { label: 'Not at all',        score: 0 },
          { label: 'Slightly',          score: 1 },
          { label: 'Moderately',        score: 2 },
          { label: 'Significantly',     score: 3 },
          { label: 'Unable to function',score: 4 },
        ],
      },
      {
        id: 'loneliness',
        label: 'How often do you feel lonely or isolated?',
        type: 'toggle',
        options: [
          { label: 'Rarely',        score: 0 },
          { label: 'Sometimes',     score: 1 },
          { label: 'Often',         score: 2 },
          { label: 'Almost always', score: 3 },
        ],
      },
    ],
  },

  {
    id: 'coping',
    stepLabel: 'Coping & Support',
    title: 'Coping & Support',
    subtitle: 'Understanding how you cope helps us point you to the right kind of support.',
    icon: '💛',
    questions: [
      {
        id: 'copingStrategies',
        label: 'Which best describes how you currently cope with stress or low mood?',
        type: 'toggle',
        options: [
          { label: 'Healthy strategies (exercise, talk, etc.)', score: 0 },
          { label: 'Mix of healthy and unhealthy',              score: 1 },
          { label: 'Mainly unhealthy (alcohol, avoidance)',     score: 2 },
          { label: 'Not coping at all',                        score: 3 },
        ],
      },
      {
        id: 'supportNetwork',
        label: 'Do you have people you can talk to about how you feel?',
        type: 'toggle',
        options: [
          { label: 'Yes — strong support',      score: 0 },
          { label: 'Some support available',    score: 1 },
          { label: 'Limited support',           score: 2 },
          { label: 'No — I feel very alone',    score: 3 },
        ],
      },
      {
        id: 'previousHelp',
        label: 'Have you previously sought professional help for mental health?',
        type: 'toggle',
        options: [
          { label: 'No, and I feel fine',        score: 0 },
          { label: 'No, but I have considered it',score: 1 },
          { label: 'Yes, and it helped',          score: 0 },
          { label: 'Yes, but I need more support',score: 2 },
        ],
      },
    ],
  },
];

// ── Review sections ────────────────────────────────────────────────────────────
export const MENTAL_REVIEW_SECTIONS = [
  { id: 'mood',    icon: '💛', label: 'Mood & Emotions',      fields: ['overallMood', 'enjoyment', 'hopelessness'] },
  { id: 'anxiety', icon: '😰', label: 'Anxiety & Worry',      fields: ['worryFreq', 'worryControl', 'panicAttacks', 'avoidance'] },
  { id: 'sleep',   icon: '😴', label: 'Sleep & Rest',         fields: ['sleepQuality', 'sleepIssueType', 'sleepImpact'] },
  { id: 'energy',  icon: '⚡', label: 'Energy & Motivation',  fields: ['energyLevel', 'motivation', 'concentration'] },
  { id: 'social',  icon: '🤝', label: 'Social & Daily Life',  fields: ['socialWithdrawal', 'dailyFunctioning', 'loneliness'] },
  { id: 'coping',  icon: '💛', label: 'Coping & Support',     fields: ['copingStrategies', 'supportNetwork', 'previousHelp'] },
];

// ── Scoring ────────────────────────────────────────────────────────────────────
const WEIGHTS = { mood:1.5, anxiety:1.3, sleep:1.0, energy:1.0, social:1.0, coping:1.2 };
const MAX_RAW  = { mood:13, anxiety:12, sleep:10, energy:9, social:10, coping:8 };

export function computeMentalScore(answers) {
  // Red flag override — immediate crisis
  if (answers.selfHarmThoughts && answers.selfHarmThoughts !== 'No, not at all') {
    return { pct: 100, crisis: true };
  }
  let weighted = 0, maxWeighted = 0;
  MENTAL_QUESTION_SCREENS.forEach((screen) => {
    const w = WEIGHTS[screen.id] ?? 1;
    const maxRaw = MAX_RAW[screen.id] ?? 10;
    let raw = 0;
    screen.questions.forEach((q) => {
      const val = answers[q.id];
      if (!val) return;
      const opt = q.options.find((o) => o.label === val);
      if (opt) raw += opt.score ?? 0;
    });
    weighted    += raw * w;
    maxWeighted += maxRaw * w;
  });
  const pct = maxWeighted > 0 ? Math.round((weighted / maxWeighted) * 100) : 0;
  return { pct, crisis: false };
}

export function getMentalRisk(pct, crisis = false) {
  if (crisis) return {
    level: 'Please Reach Out Now', emoji: '🆘', color: '#dc2626', bg: '#fef2f2', border: '#fecaca',
    desc: 'Your answers suggest you may be in distress. Please contact a crisis helpline or go to your nearest emergency department.',
    means: 'You indicated thoughts of self-harm. This is a sign that you need and deserve immediate support. You do not have to face this alone.',
    crisis: true,
    actions: [
      { icon: '📞', title: 'iCall Helpline: 9152987821',         desc: 'Free, confidential mental health support.' },
      { icon: '🏥', title: 'Visit nearest emergency department',  desc: 'If you are in immediate danger, go to A&E now.' },
      { icon: '💬', title: 'Talk to someone you trust',          desc: 'Share how you are feeling with a friend or family member.' },
    ],
  };
  if (pct < 25) return {
    level: 'Good Wellbeing', emoji: '✅', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0',
    desc: 'You appear to be coping well. Keep doing what supports your mental health.',
    means: 'Your responses suggest you are managing your mental health effectively. Maintaining good habits will help you stay resilient.',
    crisis: false,
    actions: [
      { icon: '🧘', title: 'Continue self-care routines',        desc: 'Sleep, exercise and connection keep you well.' },
      { icon: '📊', title: 'Regular wellness check-ins',        desc: 'Monitor your mood over time with our tracker.' },
      { icon: '📚', title: 'Wellbeing resources',               desc: 'Explore our library of mental health tips.' },
    ],
  };
  if (pct < 55) return {
    level: 'Mild–Moderate Distress', emoji: '💛', color: '#d97706', bg: '#fffbeb', border: '#fde68a',
    desc: 'You are experiencing some difficulties. Talking to someone could help.',
    means: 'Your responses suggest some emotional strain across several areas. Support from a counsellor or therapist could make a meaningful difference.',
    crisis: false,
    actions: [
      { icon: '💬', title: 'Book a counselling session',         desc: 'Talk through your feelings with a professional.' },
      { icon: '🧘', title: 'Mindfulness & stress tools',        desc: 'Evidence-based techniques to manage anxiety and low mood.' },
      { icon: '📱', title: 'Mental wellness app',               desc: 'Track mood, sleep and energy to spot patterns.' },
    ],
  };
  return {
    level: 'Significant Distress', emoji: '🔴', color: '#dc2626', bg: '#fef2f2', border: '#fecaca',
    desc: 'Your responses suggest significant distress. Please consider reaching out for professional support.',
    means: 'Multiple areas of your mental health are significantly affected. Speaking with a mental health professional is strongly recommended.',
    crisis: false,
    actions: [
      { icon: '👩‍⚕️', title: 'Consult a mental health professional', desc: 'A psychiatrist or psychologist can provide tailored support.' },
      { icon: '📞', title: 'Vandrevala Foundation: 1860-2662-345',  desc: '24/7 free mental health helpline.' },
      { icon: '💊', title: 'Explore treatment options',             desc: 'Therapy, medication or a combination may help.' },
    ],
  };
}
